/**
 * TagsIndex — global "Explore by tags" page (item #8).
 *
 * Layout: tag cloud where larger / bolder chips represent more popular tags.
 * Tags são exibidas em português, mantendo o label exatamente como o admin
 * cadastrou (rename inteligente é responsabilidade do painel admin).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Tag as TagIcon, Search, Loader2 } from 'lucide-react';
import { api } from '../services/api';

function pickFontSize(count, maxCount) {
  if (!maxCount) return 'text-sm';
  const ratio = count / maxCount;
  if (ratio > 0.66) return 'text-2xl font-bold';
  if (ratio > 0.33) return 'text-lg font-semibold';
  if (ratio > 0.1) return 'text-base font-medium';
  return 'text-sm';
}

function pickToneClass(count, maxCount) {
  if (!maxCount) return 'bg-slate-100 text-slate-700 hover:bg-slate-200';
  const ratio = count / maxCount;
  if (ratio > 0.66) return 'bg-[var(--primary)] text-white hover:bg-[#450066]';
  if (ratio > 0.33) return 'bg-purple-100 text-purple-800 hover:bg-purple-200';
  if (ratio > 0.1) return 'bg-orange-50 text-orange-700 hover:bg-orange-100';
  return 'bg-slate-100 text-slate-700 hover:bg-slate-200';
}

export default function TagsIndex() {
  const { municipioSlug } = useParams();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const params = municipioSlug ? { municipio_slug: municipioSlug } : {};
        const { data } = await api.get('/tags', { params });
        if (!cancelled) setTags(Array.isArray(data) ? data : []);
      } catch (_) {
        if (!cancelled) setTags([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [municipioSlug]);

  const filtered = useMemo(() => {
    if (!filter.trim()) return tags;
    const f = filter.trim().toLowerCase();
    return tags.filter((t) => t.label.toLowerCase().includes(f) || t.slug.includes(f));
  }, [tags, filter]);

  const maxCount = useMemo(() => tags.reduce((m, t) => Math.max(m, t.business_count || 0), 0), [tags]);
  const tagBase = municipioSlug ? `/${municipioSlug}/tags` : '/tags';

  return (
    <div className="min-h-screen bg-[var(--bg-secondary)]" data-testid="tags-index-page">
      <div className="max-w-5xl mx-auto px-4 py-10">
        <header className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold mb-3">
            <TagIcon className="w-3.5 h-3.5" /> Explorar por tags
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
            Encontre por interesse
          </h1>
          <p className="text-base text-gray-600 mt-2 max-w-xl mx-auto">
            Busque negócios pelas características que importam para você. Tags maiores = mais negócios marcados.
          </p>
        </header>

        <div className="max-w-xl mx-auto mb-8">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Buscar tag..."
              className="w-full pl-10 pr-4 py-3 border border-[var(--border)] rounded-full bg-white focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] text-sm"
              data-testid="tags-index-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando tags...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500" data-testid="tags-index-empty">
            Nenhuma tag encontrada{filter ? ` para "${filter}"` : ''}.
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3" data-testid="tags-index-cloud">
            {filtered.map((tag) => (
              <Link
                key={tag.slug}
                to={`${tagBase}/${tag.slug}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 transition-all active:scale-95 ${pickFontSize(tag.business_count, maxCount)} ${pickToneClass(tag.business_count, maxCount)}`}
                data-testid={`tags-index-chip-${tag.slug}`}
                title={`${tag.business_count} negócio${tag.business_count === 1 ? '' : 's'}`}
              >
                {tag.label}
                <span className="opacity-70 text-xs font-medium">({tag.business_count})</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
